import React, { PureComponent } from 'react'
import { observer } from 'mobx-react'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import i18n from 'i18next'
import Modal from '../../../../UniformComponents/Modal'
import ModalHeader from '../../../../UniformComponents/ModalHeader'
import ModalFooter from '../../../../UniformComponents/ModalFooter'
import Button from '../../../../UniformComponents/Button'
import Panel from '../../../../UniformComponents/Panel'
import './widgetCreator.scss'
import WidgetPreview from './WidgetPreview'
import DisplayOptions from './DisplayOptions'
import Typography from '../../../../UniformComponents/Typography'
import EmptyDisplayOptionsImg from '../../../../assets/images/widget_display_options.svg'

const TRASNLATION_STRINGS = {
    Metrics: 'dashboards.widgetDataOptions.title',
    Settings: 'dashboards.widgetDisplayOptions.displayOptions',
}

@observer
class WidgetCreator extends PureComponent {
    quadrantIndex = ['xAxis', 'yAxis', 'zAxis']

    constructor(props) {
        super(props)
        this.state = {
            openPanel: {
                Metrics: true,
                Settings: false,
            },
        }
    }

    toggleCollapse = (panelName) => {
        const { openPanel } = this.state
        this.setState({ openPanel: { ...openPanel, [panelName]: !openPanel[panelName] } })
    }

    getFirstMetricConfig = (widget) => {
        const result = { shouldRenderBtn: false }
        const firstMetric = widget.metrics?.[0]
        if (firstMetric) {
            const { widgetCreatorStore } = this.props
            const { isQuestionValidAsSecondaryMetric } = widgetCreatorStore
            const { question, industry, productType, surveyType } = firstMetric
            if (!question || !industry || !productType || !surveyType) return result

            const isQuestionTypeValid = isQuestionValidAsSecondaryMetric(firstMetric, question)
            if (isQuestionTypeValid) result.shouldRenderBtn = true
            result.industry = industry
            result.productType = productType
            result.surveyType = surveyType
        }
        return result
    }

    renderMetricControls = () => {
        const { widgetCreatorStore } = this.props
        const {
            renderWidgetMetricControls,
            newWidget,
            isEditSetupInProgress,
        } = widgetCreatorStore

        const config = this.getFirstMetricConfig(newWidget)
        const { shouldRenderBtn, ...newMetricConfig } = config
        if (!shouldRenderBtn && !isEditSetupInProgress) newWidget.removeAllMetricsButFirst()

        return (
            <>
                {!isEditSetupInProgress && newWidget.metrics.map((m, i) => (
                    <div className="controls">
                        {renderWidgetMetricControls(m, i)}
                    </div>
                ))}

                { shouldRenderBtn
                    ? (
                        <div style={{ paddingBottom: '8px' }} className="addMetricButton">
                            <Button
                                onClick={() => widgetCreatorStore.addMetricToWidget(newMetricConfig)}
                                label={i18n.t('dynamicDash.wizard.controlBlock.addAnotherQuestion')}
                                cssClass="tertiary regular"
                            />
                        </div>
                    )
                    : null}
            </>
        )
    }

    metricPanel = (controls, panelName, scrollable = true) => {
        const { openPanel } = this.state
        const panelOpen = openPanel[panelName]
        const collapsedTitle = i18n.t(TRASNLATION_STRINGS[panelName])
        return (
            <Panel
                style={{ overflow: 'visible' }}
                cssClass={`${panelName} metrics ${panelOpen ? '' : 'collapsed'}`}
            >
                <div
                    className="collapseIcon"
                    onClick={() => this.toggleCollapse(panelName)}
                >
                    <div
                        className="collapseIconContainer"
                    >
                        {
                            panelOpen
                                ? <i className={`fa-regular ${panelName === 'Metrics' ? 'fa-chevron-left' : 'fa-chevron-right'}`} />
                                : <i className={`fa-regular ${panelName === 'Metrics' ? 'fa-chevron-right' : 'fa-chevron-left'}`} />
                        }
                    </div>
                </div>
                <div
                    className="collapsedMenuLabel"
                    style={{ display: !panelOpen ? 'block' : 'none' }}
                >
                    {i18n.t(collapsedTitle ?? panelName)}
                </div>
                <div className="panelBody" style={scrollable ? {} : { overflowY: 'hidden' }}>
                    {controls}
                </div>
            </Panel>
        )
    }

    renderMetricSettings = () => {
        const { widgetCreatorStore } = this.props
        const {
            newWidget, renderDataLevelControls, dashStore, shouldShowDataLevelControls,
        } = widgetCreatorStore

        const renderDisplayOptions = Boolean(newWidget?.data?.[0]?.questionId)
        return (
            <div className="singleMetric">
                {this.metricPanel(
                    <>
                        <div className="widgetSectionTitle">
                            <Typography type="h3">
                                {i18n.t('dashboards.widgetDataOptions.title')}
                            </Typography>
                            <Typography type="p">
                                {i18n.t('dashboards.widgetDataOptions.desc')}
                            </Typography>
                        </div>
                        {this.renderMetricControls()}
                        {shouldShowDataLevelControls() && renderDataLevelControls()}
                    </>,
                    'Metrics',
                )}
                <WidgetPreview newWidget={newWidget} dashStore={dashStore} />
                {this.metricPanel(
                    <>
                        <Typography type="h3" cssClass="title">
                            {i18n.t('dashboards.widgetDisplayOptions.displayOptions')}
                        </Typography>
                        {renderDisplayOptions
                            ? (
                                <DisplayOptions
                                    widgetWithData={newWidget.data[0]}
                                    dashboardWizardStore={widgetCreatorStore}
                                    updatePreview={newWidget.refreshWidgetKey}
                                />
                            )
                            : (
                                <div className="emptyState">
                                    <img src={EmptyDisplayOptionsImg} alt="Display options placeholder: not enough parameters" />
                                    <Typography type="b" style={{ textAlign: 'center' }}>
                                        {i18n.t('dashboards.widgetDisplayOptions.placeholder')}
                                    </Typography>
                                </div>
                            )}
                    </>,
                    'Settings',
                    newWidget.data !== undefined,
                )}
            </div>
        )
    }

    closeShowWidgetCreator = () => {
        const { widgetCreatorStore } = this.props
        const { resetStore, toggleShowWidgetCreatorV3 } = widgetCreatorStore
        resetStore()
        toggleShowWidgetCreatorV3()
    }

    updateLayout = (newKey) => {
        const { widgetCreatorStore } = this.props
        const { dashStore, newWidget } = widgetCreatorStore
        const { previousKey } = newWidget
        const { layouts } = dashStore
        return Object.keys(layouts).reduce((layout, k) => (
            {
                ...layout,
                [k]: layouts[k]
                    .map((l) => ({ ...l, i: l.i === previousKey ? newKey : l.i })),
            }
        ), {})
    }

    updateWidget = () => {
        const { widgetCreatorStore } = this.props
        const { dashStore, newWidget } = widgetCreatorStore
        const { updateWidget, updateLayouts, saveDashboard } = dashStore
        const { previousKey } = newWidget
        const widgets = R.pathOr([{}], ['widgets'], dashStore)
        const widgetIndex = widgets.findIndex((w) => w.key === previousKey)
        const newKey = uuidv4()
        const newLayout = this.updateLayout(newKey)
        const widget = newWidget.newWidgetToWidget()
        widget.key = newKey
        updateWidget(widgetIndex, widget)
        updateLayouts(newLayout)
        saveDashboard()
        widgetCreatorStore.recordWidgetUpdated()
        this.closeShowWidgetCreator()
    }

    addWidget = () => {
        const { widgetCreatorStore } = this.props
        const { newWidget, addWidget } = widgetCreatorStore
        const { error } = this.state
        if (!newWidget.chartType) {
            this.setState({ error: { ...error, chart: true } })
            return
        }
        if (newWidget.editing) {
            this.updateWidget()
            return
        }
        addWidget()

        this.closeShowWidgetCreator()
    }

    render() {
        const { widgetCreatorStore } = this.props
        const { showWidgetCreatorV3, newWidget } = widgetCreatorStore
        const { editing } = newWidget
        return (
            <Modal
                show={showWidgetCreatorV3}
                onClose={this.closeShowWidgetCreator}
                cssClass="widgetCreatorModal dashboardCreationWizardV3 advancedPreview"
            >
                <div className="widgetCreatorContainer">
                    <ModalHeader cssClass="header">
                        <div className="title">
                            {i18n.t(`dynamicDash.wizard.title${editing ? '.edit' : ''}`)}
                        </div>
                    </ModalHeader>
                    <div className="body">
                        {this.renderMetricSettings()}
                    </div>
                    <ModalFooter>
                        <Button
                            label="Cancel"
                            cssClass="tertiary regular"
                            style={{ marginRight: '25px', width: '134px' }}
                            onClick={this.closeShowWidgetCreator}
                        />
                        <Button
                            label={i18n.t(`dynamicDash.wizard.button.${editing ? 'update' : 'add'}`)}
                            cssClass="primary regular"
                            style={{ width: '134px' }}
                            onClick={this.addWidget}
                        />
                    </ModalFooter>
                </div>
            </Modal>
        )
    }
}

export default WidgetCreator
